export interface Vec {
  x: number
  y: number
}

export type Dir = 0 | 1 | 2 | 3 // up, right, down, left

export const DIR_VECTORS: readonly Vec[] = [
  { x: 0, y: -1 },
  { x: 1, y: 0 },
  { x: 0, y: 1 },
  { x: -1, y: 0 },
]

export const DIR_NAMES = ['UP', 'RIGHT', 'DOWN', 'LEFT'] as const

export function opposite(d: Dir): Dir {
  return ((d + 2) % 4) as Dir
}

export type PickupKind = 'mote' | 'ember' | 'prism'

export interface Pickup {
  kind: PickupKind
  x: number
  y: number
  /** Ticks left before it burns out. Infinity-ish for motes. */
  ttl: number
  /** Tick it was created on — renderers use this for spawn animation. */
  born: number
}

export interface Bloom {
  x: number
  y: number
  /** Ticks in a full open/close cycle. */
  period: number
  /** How many ticks of the cycle it is open (and lethal). */
  openFor: number
  /** Cycle offset, so blooms breathe out of sync with each other. */
  phase: number
  born: number
}

export type DeathCause = 'wall' | 'bloom' | 'dark' | 'self' | 'none'

export type SimEvent =
  | { type: 'eat'; kind: PickupKind; x: number; y: number; points: number; chain: number }
  | { type: 'bite'; x: number; y: number; severed: number; points: number }
  | { type: 'bloom'; x: number; y: number }
  | { type: 'coil'; coil: number }
  | { type: 'phase'; on: boolean }
  | { type: 'chainBreak'; at: number }
  | { type: 'lowLight' }
  | { type: 'death'; cause: DeathCause; x: number; y: number }
  | { type: 'fangs' } // bite came off cooldown

export interface Rules {
  id: ModeId
  label: string
  tagline: string
  grid: number
  startLength: number
  /** Milliseconds per tick at coil 1. Presentation only — the sim is tick-based. */
  baseTickMs: number
  msPerCoil: number
  minTickMs: number
  motesPerCoil: number
  /** Light drains every tick; zero light is death. Disabled in Pure. */
  lightEnabled: boolean
  lightMax: number
  lightStart: number
  lightDrainBase: number
  lightDrainPerSegment: number
  lightDrainPerCoil: number
  moteLight: number
  emberLight: number
  prismLight: number
  biteLightPerSegment: number
  /** Running into yourself severs the tail instead of killing you. */
  biteEnabled: boolean
  biteCooldown: number
  biteMinLength: number
  emberTtl: number
  recoilTicks: number
  chainWindow: number
  chainMax: number
  prismEvery: [number, number]
  prismTtl: number
  phaseTicks: number
  bloomsEnabled: boolean
  bloomsPerCoil: number
  bloomCap: number
  maxMotes: number
}

export type ModeId = 'ouroboros' | 'pure' | 'daily'

export interface SimState {
  rulesId: ModeId
  seed: number
  tick: number
  grid: number
  snake: Vec[]
  dir: Dir
  /** Turns are buffered so fast inputs between ticks are never swallowed. */
  buffer: Dir[]
  grow: number
  light: number
  score: number
  chain: number
  chainTimer: number
  motesEaten: number
  embersEaten: number
  bites: number
  longest: number
  coil: number
  pickups: Pickup[]
  blooms: Bloom[]
  biteCooldown: number
  recoil: number
  phase: number
  nextPrism: number
  alive: boolean
  cause: DeathCause
  events: SimEvent[]
}

export interface RunSummary {
  mode: ModeId
  seed: number
  score: number
  ticks: number
  motes: number
  embers: number
  bites: number
  longest: number
  coil: number
  cause: DeathCause
}
